import React, { useState, useEffect } from "react";
import axios from "axios";

function AdminDashboard({ handleLogout }) {
  const [activeTab, setActiveTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [mobileSelectedUser, setMobileSelectedUser] = useState(null);

  const isMobile = useIsMobile(768);
  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(
      () => window.innerWidth <= breakpoint
    );
    useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, [breakpoint]);
    return isMobile;
  }

  useEffect(() => {
    const role = localStorage.getItem("role");
    setCurrentUserRole(role);
    fetchUsers();
  }, []);

  useEffect(() => {
    const delayedSearch = setTimeout(() => fetchUsers(), 300);
    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  useEffect(() => {
    let timeout;
    if (error === "Failed to authenticate token.") {
      timeout = setTimeout(() => {
        handleLogout();
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [error]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { email: searchTerm || undefined },
        }
      );
      setUsers(response.data);

      if (!searchTerm) {
        setAllUsers(response.data);
      } else {
        const allUsersResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllUsers(allUsersResponse.data);
      }

      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (user) => {
    // Use the actual user object clicked, not anything from search
    setSelectedUser({ ...user }); // clone to be safe
    setNewPassword("");
    setShowPasswordModal(true);
    setSearchTerm(""); // password is geting prefilled upon clicking the password edit button
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Delete user: ${userEmail}?`)) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admin/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchUsers();
      alert("User deleted successfully!");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const handModalClose = () => {
    setShowPasswordModal(false);
    setSearchTerm("");
  };

  const handleRoleChange = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const submitRoleChange = async () => {
    if (newRole === selectedUser.role) {
      setShowRoleModal(false);
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/users/${selectedUser.id}/role`,
        { newRole },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setShowRoleModal(false);
      fetchUsers();
      alert("Role updated successfully!");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  const submitPasswordChange = async () => {
    if (!newPassword.trim()) return alert("Enter a new password");

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/users/${selectedUser.id}/password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPasswordModal(false);
      setSearchTerm("");
      alert("Password updated!");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update password");
    } finally {
      setActionLoading(false);
    }
  };

  const getUserStats = () => {
    const stats = allUsers.reduce((acc, user) => {
      acc[user.role] = (acc[user.role] || 0) + 1;
      return acc;
    }, {});
    return {
      total: allUsers.length,
      admin: stats.admin || 0,
      superuser: stats.superuser || 0,
      user: stats.user || 0,
    };
  };

  if (loading)
    return (
      <div className="admin-container-ad">
        <h2>Loading Users...</h2>
      </div>
    );

  const stats = getUserStats();

  return (
    <>
      <main className="main">
        <div className="topbar">
          <div className="topbar__brand">
            <span className="crumbs">Admin Dashboard</span>
          </div>
        </div>
        <div className="admin-container-ad">
          {/* ================ Tabs ================ */}
          <div className="tabs-ad">
            {["users", "stats"].map((tab) => (
              <button
                key={tab}
                className={`tab-btn-ad ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
            <div
              className="tab-indicator-ad"
              style={{
                transform:
                  activeTab === "users" ? "translateX(0%)" : "translateX(100%)",
              }}
            />
          </div>

          {/* ================ Tab Content ================ */}
          <div className="tab-content-ad">
            {!isMobile && activeTab === "users" && (
              <>
                {error && <div className="message error">{error}</div>}
                <div className="search-section">
                  <input
                    type="text"
                    placeholder="Search users by email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                </div>
                <table className="users-table-ad">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>#{user.id}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-badge-ad ${user.role}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons-ad">
                            {currentUserRole === "admin" && (
                              <button
                                onClick={() => handleRoleChange(user)}
                                className="btn-small btn-role"
                                disabled={actionLoading}
                              >
                                👤 Role
                              </button>
                            )}
                            <button
                              className="btn-small btn-password"
                              onClick={() => handlePasswordChange(user)}
                              disabled={actionLoading}
                            >
                              🔑 Password
                            </button>
                            {currentUserRole === "admin" && (
                              <button
                                onClick={() =>
                                  handleDeleteUser(user.id, user.email)
                                }
                                className="btn-small btn-delete"
                                disabled={actionLoading}
                              >
                                🗑️ Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* MOBILE: Users tab */}
            {isMobile && activeTab === "users" && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {error && (
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: 10,
                      fontSize: 14,
                      background: "#fef2f2",
                      color: "#991b1b",
                      border: "1px solid #fecaca",
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Search */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "10px 12px",
                    boxShadow: "0 2px 8px rgba(16,24,40,0.04)",
                  }}
                >
                  <span style={{ opacity: 0.7 }}>🔍</span>
                  <input
                    type="text"
                    placeholder="Search users by email…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      fontSize: 14,
                      color: "#0f172a",
                    }}
                  />
                </div>

                {/* Results: list user IDs */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {users.length === 0 ? (
                    <div
                      style={{
                        padding: 20,
                        textAlign: "center",
                        color: "#64748b",
                        border: "1px dashed #e5e7eb",
                        borderRadius: 12,
                        background: "#fff",
                      }}
                    >
                      No users found
                    </div>
                  ) : (
                    users.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => {
                          setMobileSelectedUser(u);
                          setMobileModalOpen(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          background: "#fff",
                          boxShadow: "0 1px 4px rgba(16,24,40,.06)",
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        <span>{u.email}</span>
                        <span style={{ opacity: 0.5 }}>›</span>
                      </button>
                    ))
                  )}
                </div>

                {/* Mobile modal */}
                {mobileModalOpen && mobileSelectedUser && (
                  <div
                    onClick={() => setMobileModalOpen(false)}
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,.35)",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      zIndex: 9999,
                    }}
                  >
                    {/* sheet */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        maxWidth: 520,
                        background: "#fff",
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        padding: 16,
                        boxShadow: "0 -10px 30px rgba(0,0,0,.2)",
                      }}
                    >
                      <div
                        style={{
                          height: 4,
                          width: 42,
                          background: "#e5e7eb",
                          borderRadius: 999,
                          margin: "0 auto 12px",
                        }}
                      />
                      {/* <div
                        style={{
                          marginBottom: 8,
                          fontWeight: 800,
                          fontSize: 16,
                        }}
                      >
                        User #{mobileSelectedUser.id}
                      </div> */}
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          marginBottom: 24,
                          fontWeight: 800,
                          fontSize: 16,
                        }}
                      >
                        {mobileSelectedUser.email}
                      </div>

                      <div
                        style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
                      >
                        {/* Admin-only: change role */}
                        {currentUserRole === "admin" && (
                          <button
                            onClick={() => {
                              handleRoleChange(mobileSelectedUser); // opens your existing role modal
                              setMobileModalOpen(false);
                            }}
                            style={{
                              flex: 1,
                              minWidth: 120,
                              height: 40,
                              borderRadius: 10,
                              border: "1px solid #e5e7eb",
                              background: "#fff",
                              fontWeight: 700,
                            }}
                          >
                            👤 Role
                          </button>
                        )}

                        {/* Password (allowed for all as per your desktop block) */}
                        <button
                          onClick={() => {
                            handlePasswordChange(mobileSelectedUser); // opens your existing password modal
                            setMobileModalOpen(false);
                          }}
                          style={{
                            flex: 1,
                            minWidth: 120,
                            height: 40,
                            borderRadius: 10,
                            border: "1px solid #2e7cc5",
                            background: "#2e7cc5",
                            color: "#fff",
                            fontWeight: 700,
                            boxShadow: "0 4px 12px rgba(46,124,197,.25)",
                          }}
                          disabled={actionLoading}
                        >
                          🔑 Password
                        </button>

                        {/* Admin-only: delete user */}
                        {currentUserRole === "admin" && (
                          <button
                            onClick={() => {
                              handleDeleteUser(
                                mobileSelectedUser.id,
                                mobileSelectedUser.email
                              );
                              setMobileModalOpen(false);
                            }}
                            style={{
                              flex: 1,
                              minWidth: 120,
                              height: 40,
                              borderRadius: 10,
                              border: "1px solid #ef4444",
                              background: "#ef4444",
                              color: "#fff",
                              fontWeight: 700,
                              boxShadow: "0 4px 12px rgba(239,68,68,.25)",
                            }}
                            disabled={actionLoading}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => setMobileModalOpen(false)}
                        style={{
                          marginTop: 12,
                          width: "100%",
                          height: 40,
                          borderRadius: 10,
                          border: "1px solid #e5e7eb",
                          background: "#fff",
                          fontWeight: 700,
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "stats" && (
              <div className="admin-stats">
                <div className="stat-card-ad">
                  <div className="stat-number-ad">{stats.total + 1}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card-ad">
                  <div className="stat-number-ad">
                    {stats.admin == 0 ? 1 : stats.admin}
                  </div>
                  <div className="stat-label">Admins</div>
                </div>
                <div className="stat-card-ad">
                  <div className="stat-number-ad">{stats.superuser}</div>
                  <div className="stat-label">Superusers</div>
                </div>
                <div className="stat-card-ad">
                  <div className="stat-number-ad">{stats.user}</div>
                  <div className="stat-label">Users</div>
                </div>
              </div>
            )}
          </div>

          {/* ================ Modals ================ */}
          {/* {showPasswordModal && selectedUser && (
            <div className="modal-overlay">
              <div className="modal-ad">
                <h3>Change Password for {selectedUser.email}</h3>
                <input
                  type="password"
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <div className="modal-actions">
                  <button
                    onClick={submitPasswordChange}
                    disabled={actionLoading}
                  >
                    Save
                  </button>
                  <button onClick={handModalClose}>Cancel</button>
                </div>
              </div>
            </div>
          )} */}

          {showPasswordModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>🔑 Change Password for {selectedUser?.email}</h3>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="modal-buttons">
                  <button
                    onClick={handModalClose}
                    className="btn btn-secondary h-3rem"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitPasswordChange}
                    className="btn btn-primary h-3rem"
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showRoleModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>👤 Change Role for {selectedUser?.email}</h3>
                <div className="form-group">
                  <label className="form-label">Select New Role</label>
                  <select
                    className="form-input"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="user">User</option>
                    {currentUserRole === "admin" && (
                      <>
                        <option value="superuser">Superuser</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="modal-buttons">
                  <button
                    onClick={() => setShowRoleModal(false)}
                    className="btn btn-secondary h-3rem"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitRoleChange}
                    className="btn btn-primary h-3rem"
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Updating..." : "Update Role"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

export default AdminDashboard;
